import React, { forwardRef, PropsWithChildren, useEffect } from 'react';
import { InspirationalTemplate as Template } from '../../domain/entities/inspirationalTemplate';
import s from './InspirationalTemplate.scss';
import { useTranslation } from '@wix/wix-i18n-config';
import { TemplateImage } from '../TemplateImage/TemplateImage';
import classNames from 'classnames';
import { QuickViewButton } from '../QuickViewButton/QuickViewButton';
import { useInViewPort } from '../../hooks/useInViewPort';
import { useBiLogger } from '../../contexts/BILoggerContext';
import { CategorySlug } from '../../config';
import { useInitialState } from '../../contexts/InitialStateContext';
import { TemplateVideo } from '../TemplateVideo/TemplateVideo';

interface InspirationalTemplateProps {
  template: Template;
  backgroundColor?: string;
  className?: string;
  withTag?: boolean;
  index?: number;
}

export const InspirationalTemplate = forwardRef<
  HTMLDivElement,
  PropsWithChildren<InspirationalTemplateProps>
>(({ template, backgroundColor, className, withTag, index }, ref) => {
  const { t } = useTranslation();
  const biLogger = useBiLogger();
  const inViewPort = useInViewPort(ref as any, { threshold: 1 });
  const { isMobile } = useInitialState();

  useEffect(() => {
    if (!inViewPort) {
      return;
    }
    biLogger.reportAboveFoldImpression({
      templateId: template.templateId,
      slug: template.slug,
      title: template.title,
      categorySlug: CategorySlug.INSPIRATIONAL,
      index,
    });
  }, [biLogger, inViewPort, index, template]);

  return (
    <div
      style={{ backgroundColor }}
      className={classNames(s.container, className)}
      ref={ref}
    >
      <div className={s.templateImageContainer}>
        {!isMobile ? (
          <TemplateVideo
            className={s.templateImage}
            template={template}
            categorySlug={CategorySlug.INSPIRATIONAL}
          />
        ) : (
          <TemplateImage
            className={s.templateImage}
            template={template}
            categorySlug={CategorySlug.INSPIRATIONAL}
            isLazyLoad={false}
          />
        )}
      </div>
      <div className={s.templateInfoContainer}>
        <div className={s.contentContainer}>
          <div className={s.title}>{template.title}</div>
          <div className={s.description}>{template.description}</div>
        </div>
        {withTag && (
          <div className={s.tag}>
            {t('app.templates.carousel.template.tag')}
          </div>
        )}
        <div className={s.quickViewWrapper}>
          <QuickViewButton template={template} index={index} />
        </div>
      </div>
    </div>
  );
});
